import DownloadIcon from '@mui/icons-material/Download'
import { Box, Button, Card, CardContent, CircularProgress, Stack, Tab, Tabs, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import dayjs, { type Dayjs } from 'dayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { useAppSelector } from '../store'
import { API_BASE_URL } from '../config/api'
import PageHeader from '../components/PageHeader'
import ReloadButton from '../components/ReloadButton'
import SamplingChooserView from './quality/SamplingChooserView'
import SamplingDataListView from './quality/SamplingDataListView'
import PssLabelView from './quality/PssLabelView'
import NestleSampleListView from './quality/NestleSampleListView'
import ComingSoonPage from './ComingSoonPage'

const TAB_CONFIG = [
  { path: 'sampling', label: 'Sampling' },
  { path: 'sampling-data', label: 'Sampling Data' },
  { path: 'pss-label', label: 'Pr PSS Label' },
  { path: 'nestle-sample', label: 'Nestle Sample' },
] as const

const headerButtonSx = {
  minHeight: 24,
  px: 0.75,
  py: 0.15,
  fontSize: '0.67rem',
  lineHeight: 1.1,
  textTransform: 'none' as const,
}

const headerLabelSx = {
  whiteSpace: 'nowrap',
  fontSize: '0.72rem',
  lineHeight: 1.1,
}

const compactInputSx = {
  width: { xs: 130, sm: 148 },
  '& .MuiInputBase-root': {
    minHeight: 26,
    fontSize: '0.72rem',
  },
  '& .MuiInputBase-input': {
    py: 0.25,
    px: 0.75,
  },
}

/**
 * Access fiscal-year default for `module='quality_control'` (DocConvert_Quality_overview §4.1):
 *  - If current month >= Oct: start = Oct 1 current year, end = Sep 30 next year.
 *  - Else: start = Oct 1 previous year, end = Sep 30 current year.
 */
function fiscalYearDefault(now: Dayjs = dayjs()): { startDate: string; endDate: string } {
  const month = now.month() + 1
  const year = now.year()
  if (month >= 10) {
    return { startDate: `${year}-10-01`, endDate: `${year + 1}-09-30` }
  }
  return { startDate: `${year - 1}-10-01`, endDate: `${year}-09-30` }
}

const getDownloadFilename = (disposition: string | null): string | null => {
  if (!disposition) return null
  const filenameStar = /filename\*\s*=\s*([^;]+)/i.exec(disposition)
  if (filenameStar?.[1]) {
    const value = filenameStar[1].trim().replace(/^UTF-8''/i, '').replace(/^"|"$/g, '')
    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }
  const filename = /filename\s*=\s*([^;]+)/i.exec(disposition)
  if (!filename?.[1]) return null
  return filename[1].trim().replace(/^"|"$/g, '')
}

export default function QualityControlPage() {
  const location = useLocation()
  const navigate = useNavigate()

  const fyDefault = useMemo(() => fiscalYearDefault(), [])
  const [startDate, setStartDate] = useState(fyDefault.startDate)
  const [endDate, setEndDate] = useState(fyDefault.endDate)
  const [exporting, setExporting] = useState<'all' | 'filter' | null>(null)
  const email = useAppSelector((s) => s.auth.user?.email ?? '')
  const exportToken = 'JDHK-NDEB-DBCK-DGEJ'

  const activeIndex = useMemo(() => {
    const segment = location.pathname.split('/').pop() ?? ''
    const idx = TAB_CONFIG.findIndex((t) => t.path === segment)
    return idx >= 0 ? idx : 0
  }, [location.pathname])

  useEffect(() => {
    if (!location.pathname.startsWith('/quality')) return
    const segment = location.pathname.split('/').filter(Boolean)[1] ?? ''
    if (!TAB_CONFIG.some((t) => t.path === segment)) {
      navigate('/quality/sampling', { replace: true })
    }
  }, [location.pathname, navigate])

  const hasInvalidRange = Boolean(startDate) && Boolean(endDate) && startDate > endDate

  const downloadQualityExcel = async (mode: 'all' | 'filter', url: string) => {
    if (!email) return
    setExporting(mode)
    try {
      const res = await fetch(url)
      if (!res.ok) throw new Error('Export failed')
      const blob = await res.blob()
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const filename = getDownloadFilename(res.headers.get('content-disposition'))
      const downloadName =
        mode === 'filter' && filename ? filename.replace(/\.xlsx$/i, '_filter.xlsx') : filename
      a.href = href
      a.download = downloadName ?? (mode === 'filter' ? 'Quality_filter.xlsx' : 'Quality.xlsx')
      a.click()
      URL.revokeObjectURL(href)
    } finally {
      setExporting(null)
    }
  }

  const handleExportAll = async () => {
    const url = `${API_BASE_URL}/erp-legacy/download/quality/data?token=${encodeURIComponent(exportToken)}&email=${encodeURIComponent(email)}`
    await downloadQualityExcel('all', url)
  }

  const handleExportFilter = async () => {
    const url = `${API_BASE_URL}/erp-legacy/download/quality/data_filter?token=${encodeURIComponent(exportToken)}&email=${encodeURIComponent(email)}&start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`
    await downloadQualityExcel('filter', url)
  }

  const current = TAB_CONFIG[activeIndex]

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <PageHeader
        title="Quality Control"
        module="Quality"
        actions={
          <Stack
            direction="row"
            spacing={0.45}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
            justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
            sx={{ width: '100%' }}
          >
            <Stack direction="row" spacing={0.45} alignItems="center" useFlexGap flexWrap="wrap">
              <Typography variant="body2" fontWeight={700} sx={headerLabelSx}>
                Date:
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  format="DD-MMM-YYYY"
                  value={startDate ? dayjs(startDate) : null}
                  onChange={(d) => setStartDate(d ? d.format('YYYY-MM-DD') : '')}
                  slotProps={{ textField: { size: 'small', sx: compactInputSx, inputProps: { 'aria-label': 'Quality start date' } } }}
                />
                <DatePicker
                  format="DD-MMM-YYYY"
                  value={endDate ? dayjs(endDate) : null}
                  onChange={(d) => setEndDate(d ? d.format('YYYY-MM-DD') : '')}
                  slotProps={{ textField: { size: 'small', error: hasInvalidRange, sx: compactInputSx, inputProps: { 'aria-label': 'Quality end date' } } }}
                />
              </LocalizationProvider>
            </Stack>

            <ReloadButton sx={headerButtonSx} apiTags={[]} baseTags={[]} />
            <Button
              size="small"
              variant="outlined"
              sx={headerButtonSx}
              onClick={handleExportFilter}
              disabled={exporting !== null || !email || hasInvalidRange}
              startIcon={
                exporting === 'filter' ? (
                  <CircularProgress size={11} color="inherit" />
                ) : (
                  <DownloadIcon fontSize="small" />
                )
              }
            >
              Export (Filter)
            </Button>
            <Button
              size="small"
              variant="outlined"
              sx={headerButtonSx}
              onClick={handleExportAll}
              disabled={exporting !== null || !email}
              startIcon={
                exporting === 'all' ? (
                  <CircularProgress size={11} color="inherit" />
                ) : (
                  <DownloadIcon fontSize="small" />
                )
              }
            >
              Export (All)
            </Button>
          </Stack>
        }
      />
      {hasInvalidRange && (
        <Typography variant="caption" color="error" sx={{ alignSelf: 'flex-end', mb: 1 }}>
          Start date must be before or equal to end date
        </Typography>
      )}
      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={activeIndex}
          onChange={(_, value: number) => {
            navigate(`/quality/${TAB_CONFIG[value].path}`, { replace: false })
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 38,
            '& .MuiTabs-indicator': { height: 2 },
            '& .MuiTab-root': {
              minHeight: 38,
              py: 0.25,
              px: 1.5,
              fontSize: '0.8rem',
              fontWeight: 600,
              textTransform: 'none',
            },
          }}
        >
          {TAB_CONFIG.map((tab) => (
            <Tab key={tab.path} label={tab.label} />
          ))}
        </Tabs>
        <CardContent sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', pt: 2 }}>
          {/*
            All Quality tabs are parked behind ComingSoonPage until the rebuild lands.
            Original view components (`SamplingChooserView`, `SamplingDataListView`,
            `PssLabelView`, `NestleSampleListView`) are still imported so restoring is a
            paste-back of this block:

              {current.path === 'sampling' ? (
                <SamplingChooserView />
              ) : current.path === 'sampling-data' ? (
                <SamplingDataListView />
              ) : current.path === 'pss-label' ? (
                <PssLabelView />
              ) : current.path === 'nestle-sample' ? (
                <NestleSampleListView />
              ) : null}
          */}
          <ComingSoonPage title={current.label} embedded />
        </CardContent>
      </Card>
    </Box>
  )
}
